import { fillCopy, type QuestionnaireCopy } from "./copy";
import { type QuestionnaireScreen, SELF_REROUTING_MODES } from "./flow";
import type { CoPassengerAnswer, PassengerAnswers, QuestionnaireLocale } from "./types";

/** Answers as they are being given (signature page) or as stored (admin): anything may be missing. */
type AnyAnswers = Partial<PassengerAnswers>;

/** The values a screen's title can mention. */
export type CopyValues = { airline: string; incident: string };

const CANCELLATION_TYPES = ["FLIGHT_CANCELLATION", "PNR_SEGMENT_CANCELLATION", "COVID_ORDINANCE_2020"];

/** « Quand avez-vous appris {l'annulation de votre vol} ? », « …auprès de {Air France} ? » */
export function copyValuesFor(
  copy: QuestionnaireCopy,
  claimTypeName: string | null | undefined,
  airline: string | null | undefined,
): CopyValues {
  const type = claimTypeName ?? "";
  const incident = CANCELLATION_TYPES.includes(type)
    ? copy.incidentNouns.cancellation
    : type === "DELAY_FROM_PNR_SCHEDULE"
      ? copy.incidentNouns.scheduleChange
      : copy.incidentNouns.default;

  return { airline: airline ?? "", incident };
}

export function screenTitle(
  copy: QuestionnaireCopy,
  screen: QuestionnaireScreen,
  values: CopyValues,
): string {
  return fillCopy(copy.screens[screen].title, values);
}

function coPassengerLine(
  copy: QuestionnaireCopy,
  passenger: CoPassengerAnswer,
  locale: QuestionnaireLocale,
): string {
  const status =
    passenger.status === "EMAIL" && passenger.email
      ? passenger.email
      : copy.coPassengerStatus[passenger.status].label;

  // French typography puts a space before the colon, English does not.
  return `${`${passenger.firstName} ${passenger.lastName}`.trim()}${locale === "fr" ? " :" : ":"} ${status}`;
}

/**
 * What the recap shows for one screen. `null` for a screen with nothing to show back (the recap
 * itself).
 */
export function summarizeAnswer(
  copy: QuestionnaireCopy,
  screen: QuestionnaireScreen,
  answers: AnyAnswers,
  locale: QuestionnaireLocale,
): string | null {
  const none = copy.ui.noAnswer;
  const join = (labels: string[]) => (labels.length ? labels.join(", ") : none);

  switch (screen) {
    case "VERIFICATION":
      return answers.verification ? copy.verification[answers.verification.status].label : none;
    case "CORRECTION": {
      const items = (answers.verification?.corrections ?? []).map((item) => copy.corrections[item].label);
      const details = answers.verification?.details?.trim();

      return details ? `${join(items)} · « ${details} »` : join(items);
    }
    case "NOT_TRAVELLED": {
      const reason = answers.verification?.notTravelledReason;
      return reason ? copy.notTravelled[reason].label : none;
    }
    case "PRE_EXISTING_CLAIM":
      return typeof answers.preExistingClaim === "boolean"
        ? copy.preExistingClaim[answers.preExistingClaim ? "yes" : "no"].label
        : none;
    case "REROUTING":
      // Asked as yes or no: anything but the airline's flight is « Non », detailed on the next line.
      return answers.rerouting
        ? copy.rerouting[answers.rerouting.mode === "AIRLINE" ? "AIRLINE" : "NOT_REROUTED"].label
        : none;
    case "REROUTING_MEANS": {
      const mode = answers.rerouting?.mode;
      return mode && SELF_REROUTING_MODES.includes(mode) ? copy.rerouting[mode].label : none;
    }
    case "ARRIVAL": {
      const delay = answers.rerouting?.arrivalDelay;
      const flightNumber = answers.rerouting?.flightNumber;
      const label = delay ? copy.arrival[delay].label : none;

      return flightNumber ? `${label} · ${copy.ui.flightNumberShort} ${flightNumber}` : label;
    }
    case "INFORMATION_TIMING": {
      const timing = answers.passengerInformation?.timing;
      return timing ? copy.informationTiming[timing].label : none;
    }
    case "INFORMATION_CHANNEL": {
      const channel = answers.passengerInformation?.channel;
      return channel ? copy.informationChannel[channel].label : none;
    }
    case "PRIOR_STEPS":
      return join((answers.priorSteps?.steps ?? []).map((step) => copy.priorSteps[step].label));
    case "PRIOR_STEPS_DETAILS": {
      const parts = [
        answers.priorSteps?.claimReference?.trim(),
        typeof answers.priorSteps?.compensationAmount === "number"
          ? `${answers.priorSteps.compensationAmount} €`
          : null,
      ].filter(Boolean) as string[];

      return join(parts);
    }
    case "CO_PASSENGERS": {
      // One passenger per line: a sentence of six names and statuses is unreadable.
      const lines = (answers.coPassengers ?? []).map((passenger) => coPassengerLine(copy, passenger, locale));
      return lines.length ? lines.join("\n") : none;
    }
    case "EXPENSES":
      return typeof answers.expenses?.hasExpenses === "boolean"
        ? (answers.expenses.hasExpenses ? copy.expenses.yes : copy.expenses.no).label
        : none;
    case "AIRLINE_REASON":
      if (!answers.airlineReason) {
        return none;
      }

      return answers.airlineReasonDetails
        ? `${copy.airlineReason[answers.airlineReason].label} · « ${answers.airlineReasonDetails} »`
        : copy.airlineReason[answers.airlineReason].label;
    case "CONSEQUENCES":
      return join((answers.consequences ?? []).map((item) => copy.consequences[item].label));
    case "OTHER_ISSUES":
      return join((answers.otherIssues ?? []).map((item) => copy.otherIssues[item].label));
    case "PRECISIONS":
      return answers.precisions?.text?.trim() || none;
    case "RECAP":
      return null;
  }
}

/** The screens a stored set of answers says something about, in the questionnaire's order (admin). */
export function listAnsweredScreens(answers: AnyAnswers): QuestionnaireScreen[] {
  const given: [QuestionnaireScreen, unknown][] = [
    // « Confirmer l'incident » confirms it: only a correction or « je n'ai pas pris ce vol » says something.
    ["VERIFICATION", answers.verification && answers.verification.status !== "CONFIRMED"],
    ["CORRECTION", answers.verification?.corrections?.length],
    ["NOT_TRAVELLED", answers.verification?.notTravelledReason],
    ["PRE_EXISTING_CLAIM", typeof answers.preExistingClaim === "boolean"],
    ["REROUTING", answers.rerouting],
    ["REROUTING_MEANS", answers.rerouting && SELF_REROUTING_MODES.includes(answers.rerouting.mode)],
    ["ARRIVAL", answers.rerouting?.arrivalDelay || answers.rerouting?.flightNumber],
    ["INFORMATION_TIMING", answers.passengerInformation],
    ["INFORMATION_CHANNEL", answers.passengerInformation?.channel],
    ["PRIOR_STEPS", answers.priorSteps?.steps.length],
    [
      "PRIOR_STEPS_DETAILS",
      answers.priorSteps?.claimReference || typeof answers.priorSteps?.compensationAmount === "number",
    ],
    ["CO_PASSENGERS", answers.coPassengers?.length],
    ["EXPENSES", answers.expenses],
    ["AIRLINE_REASON", answers.airlineReason],
    ["CONSEQUENCES", answers.consequences?.length],
    ["OTHER_ISSUES", answers.otherIssues?.length],
    ["PRECISIONS", answers.precisions?.text],
  ];

  return given.filter(([, value]) => Boolean(value)).map(([screen]) => screen);
}

/** Stored answers are free JSON: only something shaped like answers is read as such. */
export function readPassengerAnswers(value: unknown): PassengerAnswers | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const candidate = value as Partial<PassengerAnswers>;

  return candidate.verification && typeof candidate.verification.status === "string"
    ? (candidate as PassengerAnswers)
    : null;
}
